import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Trophy,
  Award,
  Flame,
  TrendingUp,
  Users,
  Medal,
  Target,
  BarChart3,
  Crown,
  Star,
  Zap,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  Download,
  Calendar,
  FileCheck,
  Brain,
  Shield,
  Scale,
  Lock,
  Server,
  Globe,
  AlertTriangle,
  Database,
  Code,
  Cloud
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

// Safe date formatting helper
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString();
  } catch {
    return 'N/A';
  }
};

// Badge icon mapping
const BADGE_ICONS = {
  brain: Brain,
  shield: Shield,
  scale: Scale,
  lock: Lock,
  server: Server,
  globe: Globe,
  'alert-triangle': AlertTriangle,
  database: Database,
  code: Code,
  cloud: Cloud,
  award: Award,
  trophy: Trophy,
  target: Target,
  zap: Zap,
  star: Star,
};

const getBadgeIcon = (iconName) => {
  const IconComponent = BADGE_ICONS[iconName] || Award;
  return <IconComponent className="h-5 w-5" />;
};

// Maturity level colors
const LEVEL_COLORS = {
  1: 'bg-zinc-500',
  2: 'bg-blue-500',
  3: 'bg-purple-500',
  4: 'bg-orange-500',
  5: 'bg-navigate-gold'
};

export default function GamificationAnalyticsAdmin({ token }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pathways, setPathways] = useState({});

  useEffect(() => {
    fetchAnalytics();
    fetchPathways();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/tenant-admin/gamification/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load gamification analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchPathways = async () => {
    try {
      const response = await axios.get(`${API}/admin/learning-pathways`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pathwayMap = {};
      response.data.forEach(p => {
        pathwayMap[p.pathway_id] = p.title;
      });
      setPathways(pathwayMap);
    } catch (error) {
      console.error('Failed to fetch pathways:', error);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalytics();
  };

  const getBadgeName = (badgeKey) => {
    if (!analytics?.all_badges) return badgeKey;
    const allBadges = {
      ...analytics.all_badges.pathway,
      ...analytics.all_badges.simulation,
      ...analytics.all_badges.momentum
    };
    return allBadges[badgeKey]?.name || badgeKey;
  };

  const getBadgeInfo = (badgeKey) => {
    if (!analytics?.all_badges) return null;
    return analytics.all_badges.pathway[badgeKey] || 
           analytics.all_badges.simulation[badgeKey] || 
           analytics.all_badges.momentum[badgeKey];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-navigate-gold"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
        <p className="text-zinc-400">Failed to load analytics data</p>
        <Button onClick={handleRefresh} variant="outline" className="mt-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  const { gc_stats, badge_stats, pathway_stats, streak_stats, level_distribution, top_performers, recent_badges, recent_certificates, activity_trend } = analytics;

  // Calculate totals for charts
  const totalLevelUsers = level_distribution?.reduce((acc, l) => acc + l.count, 0) || 0;

  return (
    <div className="space-y-6" data-testid="gamification-analytics-admin">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Trophy className="h-7 w-7 text-navigate-gold" />
            Gamification Analytics
          </h2>
          <p className="text-zinc-400 mt-1">Track your team's learning engagement and achievements</p>
        </div>
        <Button 
          onClick={handleRefresh} 
          variant="outline" 
          className="border-zinc-600"
          disabled={refreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Users */}
        <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <Users className="h-8 w-8 text-blue-400" />
            <span className="text-xs text-blue-400 bg-blue-500/20 px-2 py-1 rounded-full">Active</span>
          </div>
          <p className="text-3xl font-bold text-white mt-4">{gc_stats?.total_users || 0}</p>
          <p className="text-sm text-zinc-400 mt-1">Total Learners</p>
        </div>

        {/* Total GC Issued */}
        <div className="bg-gradient-to-br from-navigate-gold/20 to-orange-600/10 border border-navigate-gold/30 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <Star className="h-8 w-8 text-navigate-gold" />
            <TrendingUp className="h-5 w-5 text-green-400" />
          </div>
          <p className="text-3xl font-bold text-white mt-4">{(gc_stats?.total_gc_issued || 0).toLocaleString()}</p>
          <p className="text-sm text-zinc-400 mt-1">Total GC Distributed</p>
        </div>

        {/* Average GC */}
        <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <BarChart3 className="h-8 w-8 text-purple-400" />
            <span className="text-xs text-purple-400">Per User</span>
          </div>
          <p className="text-3xl font-bold text-white mt-4">{gc_stats?.average_gc || 0}</p>
          <p className="text-sm text-zinc-400 mt-1">Average GC</p>
        </div>

        {/* Active Streaks */}
        <div className="bg-gradient-to-br from-red-500/20 to-orange-600/10 border border-red-500/30 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <Flame className="h-8 w-8 text-red-400" />
            <span className="text-xs text-red-400 bg-red-500/20 px-2 py-1 rounded-full">
              Max: {streak_stats?.max_streak || 0}
            </span>
          </div>
          <p className="text-3xl font-bold text-white mt-4">{streak_stats?.active_streaks || 0}</p>
          <p className="text-sm text-zinc-400 mt-1">Active Streaks</p>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Leaderboard */}
        <div className="lg:col-span-2 space-y-6">
          {/* Top Performers */}
          <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <Crown className="h-5 w-5 text-navigate-gold" />
              Top Performers
            </h3>
            {top_performers?.length > 0 ? (
              <div className="space-y-3">
                {top_performers.map((user, idx) => (
                  <div 
                    key={user.user_id}
                    className={`flex items-center gap-4 p-3 rounded-lg ${
                      idx === 0 ? 'bg-navigate-gold/10 border border-navigate-gold/30' :
                      idx === 1 ? 'bg-zinc-600/20 border border-zinc-600/30' :
                      idx === 2 ? 'bg-orange-500/10 border border-orange-500/30' :
                      'bg-zinc-800/50'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      idx === 0 ? 'bg-navigate-gold text-black' :
                      idx === 1 ? 'bg-zinc-400 text-black' :
                      idx === 2 ? 'bg-orange-500 text-black' :
                      'bg-zinc-700 text-zinc-300'
                    }`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{user.full_name}</p>
                      <p className="text-xs text-zinc-500 truncate">{user.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-navigate-gold font-bold">{user.total_gc} GC</p>
                      <p className="text-xs text-zinc-500">{user.maturity_title}</p>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-zinc-400">
                      <span className="flex items-center gap-1">
                        <Flame className="h-3 w-3 text-orange-400" />
                        {user.current_streak}
                      </span>
                      <span className="flex items-center gap-1">
                        <Target className="h-3 w-3 text-purple-400" />
                        {user.pathways_completed}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-500 text-center py-8">No learner data yet</p>
            )}
          </div>

          {/* Level Distribution */}
          <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <TrendingUp className="h-5 w-5 text-purple-400" />
              Professional Level Distribution
            </h3>
            {level_distribution?.length > 0 ? (
              <div className="space-y-3">
                {analytics.maturity_levels?.map(level => {
                  const levelData = level_distribution.find(l => l.level === level.level);
                  const count = levelData?.count || 0;
                  const percentage = totalLevelUsers > 0 ? (count / totalLevelUsers * 100) : 0;
                  
                  return (
                    <div key={level.level} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-300">
                          Level {level.level}: {level.title}
                        </span>
                        <span className="text-zinc-400">{count} users ({percentage.toFixed(1)}%)</span>
                      </div>
                      <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${LEVEL_COLORS[level.level]} transition-all duration-500`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-zinc-500 text-center py-8">No level data yet</p>
            )}
          </div>

          {/* Pathway Completions */}
          <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <Target className="h-5 w-5 text-green-400" />
              Pathway Completions
            </h3>
            {pathway_stats?.length > 0 ? (
              <div className="space-y-3">
                {pathway_stats.map((pathway, idx) => (
                  <div key={pathway.pathway_id} className="flex items-center gap-4 p-3 bg-zinc-800/50 rounded-lg">
                    <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
                      <Target className="h-4 w-4 text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">
                        {pathways[pathway.pathway_id] || pathway.pathway_id}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-green-400 font-bold">{pathway.completions}</p>
                      <p className="text-xs text-zinc-500">completions</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-500 text-center py-8">No pathway completions yet</p>
            )}
          </div>
        </div>

        {/* Right Column - Badges & Recent Activity */}
        <div className="space-y-6">
          {/* Badge Distribution */}
          <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <Medal className="h-5 w-5 text-navigate-gold" />
              Badge Distribution
            </h3>
            {badge_stats?.length > 0 ? (
              <div className="space-y-3">
                {badge_stats.map((badge, idx) => {
                  const badgeInfo = getBadgeInfo(badge.badge_key);
                  return (
                    <div key={badge.badge_key} className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
                      <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400">
                        {getBadgeIcon(badgeInfo?.icon)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm truncate">
                          {getBadgeName(badge.badge_key)}
                        </p>
                        <p className="text-xs text-zinc-500">{badgeInfo?.category || 'Badge'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-purple-400 font-bold">{badge.count}</p>
                        <p className="text-xs text-zinc-500">earned</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-zinc-500 text-center py-8">No badges earned yet</p>
            )}
          </div>

          {/* Recent Badges */}
          <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <Award className="h-5 w-5 text-yellow-400" />
              Recent Badge Awards
            </h3>
            {recent_badges?.length > 0 ? (
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {recent_badges.slice(0, 10).map((badge, idx) => (
                  <div key={badge.badge_id || idx} className="flex items-center gap-3 p-2 bg-zinc-800/30 rounded-lg">
                    <div className="w-8 h-8 bg-yellow-500/20 rounded-full flex items-center justify-center text-yellow-400">
                      <Award className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{badge.user_name}</p>
                      <p className="text-xs text-zinc-500 truncate">{badge.name}</p>
                    </div>
                    <span className="text-xs text-zinc-500">
                      {formatDate(badge.earned_at)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-500 text-center py-8">No recent badges</p>
            )}
          </div>

          {/* Recent Certificates */}
          <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <FileCheck className="h-5 w-5 text-green-400" />
              Recent Certificates
            </h3>
            {recent_certificates?.length > 0 ? (
              <div className="space-y-3 max-h-[250px] overflow-y-auto">
                {recent_certificates.map((cert, idx) => (
                  <div key={cert.certificate_id || idx} className="flex items-center gap-3 p-2 bg-zinc-800/30 rounded-lg">
                    <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center text-green-400">
                      <FileCheck className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{cert.user_name}</p>
                      <p className="text-xs text-zinc-500 truncate">{cert.pathway_name}</p>
                    </div>
                    <span className="text-xs text-zinc-500">
                      {formatDate(cert.issue_date)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-500 text-center py-8">No certificates issued yet</p>
            )}
          </div>

          {/* Streak Stats */}
          <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <Flame className="h-5 w-5 text-orange-400" />
              Streak Statistics
            </h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-orange-400">{streak_stats?.active_streaks || 0}</p>
                <p className="text-xs text-zinc-400">Active</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{streak_stats?.average_streak || 0}</p>
                <p className="text-xs text-zinc-400">Avg Days</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-red-400">{streak_stats?.max_streak || 0}</p>
                <p className="text-xs text-zinc-400">Best Streak</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Trend */}
      {activity_trend?.length > 0 && (
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
            <Calendar className="h-5 w-5 text-blue-400" />
            30-Day Activity Trend
          </h3>
          <div className="h-40 flex items-end gap-1">
            {activity_trend.map((day, idx) => {
              const maxGC = Math.max(...activity_trend.map(d => d.gc_earned));
              const height = maxGC > 0 ? (day.gc_earned / maxGC * 100) : 0;
              return (
                <div 
                  key={day.date}
                  className="flex-1 bg-blue-500/50 rounded-t hover:bg-blue-400/60 transition-colors cursor-pointer group relative"
                  style={{ height: `${Math.max(height, 5)}%` }}
                  title={`${day.date}: ${day.gc_earned} GC`}
                >
                  <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block bg-zinc-700 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                    {day.date}: {day.gc_earned} GC
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-xs text-zinc-500 mt-2">
            <span>{activity_trend[0]?.date}</span>
            <span>{activity_trend[activity_trend.length - 1]?.date}</span>
          </div>
        </div>
      )}
    </div>
  );
}
